import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useClient } from 'hooks/useClient';
import { readApiError } from '@/state/billing/api-error';

export type SentimentEstimate = {
  /** AI credits one comment costs, measured from the ledger once there is enough of it, else a default. */
  perComment: number;
  /** Comments in the last 30 days, and what they would cost: a guide to a month going forward. */
  monthlyComments: number;
  monthlyCredits: number;
  /** Comments collected before the switch went on (or never classified), and what classifying them would cost. */
  backlogComments: number;
  backlogCredits: number;
};

type UseSentimentEstimateParams = { surveyId: number };

/**
 * What sentiment analysis costs this survey in AI credits, and the one action that follows
 * from it: queueing the comments collected before the switch went on. Turning the switch on
 * skips those so a single click cannot spend a large amount; this is the explicit way.
 */
export const useSentimentEstimate = ({ surveyId }: UseSentimentEstimateParams) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const [isConfirming, setIsConfirming] = useState(false);
  const key = ['SURVEY_SENTIMENT_ESTIMATE', surveyId];

  const { data: estimate } = useQuery<SentimentEstimate>({
    queryKey: key,
    queryFn: () => apiClient.get(`/surveys/${surveyId}/sentiment-estimate`),
  });

  const analyseExisting = useMutation({
    mutationFn: () => apiClient.post<{ queued: number }>(`/surveys/${surveyId}/analyse-existing`),
    onSuccess: ({ queued }) => {
      toast.success(
        queued > 0
          ? `${queued.toLocaleString()} comments queued. They are analysed over the next few minutes.`
          : 'There was nothing to analyse.',
      );
      setIsConfirming(false);
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error: unknown) => {
      toast.error('Could not queue the comments', { description: readApiError(error).message ?? undefined });
    },
  });

  return {
    estimate,
    isConfirming,
    openConfirm: () => setIsConfirming(true),
    closeConfirm: () => setIsConfirming(false),
    analyseExisting: () => analyseExisting.mutateAsync(),
  };
};
